import { AutomationStep } from '../models/automation-step.model';
import { SensitivityLevel, SensitivityMeta } from '../models/agent-sensitivity.model';
import { AgentRunStatus } from '../models/agent-run.model';
import {
  AgentActivation,
  AgentActivationMeta,
  AgentAutonomy,
  AgentCadence,
  AgentKpiBaseline,
  AgentKpiDirection,
  AgentReversibility,
  AgentReversibilityMeta,
  AgentRunStatusMeta,
  AgentStage,
  AgentStageMeta,
  AgentTaxonomyMeta
} from '../models/agent-taxonomy.model';

/**
 * The display tables behind the agent taxonomy, transcribed from the SOA Design Kit prototype.
 *
 * Colours are deliberately absent: every table carries a `tone`, and the SCSS maps tone to a
 * variable from `colors.scss`. Putting a hex here would put it in an inline style, out of reach of
 * the theme.
 */

/** Ordered, so the library landing always groups stages the same way the planning service runs. */
export const AGENT_STAGE_ORDER: AgentStage[] = ['demand', 'inventory', 'replenishment'];

/**
 * The mark on the Service filter's "All" chip.
 *
 * Not part of `AGENT_STAGE_META` because "all" is the absence of a stage, not a fourth one — the
 * chip row is the only place it is rendered. It is the design kit's `applications` icon, taken from
 * the prototype bundle exactly like the three stage marks beside it.
 */
export const AGENT_STAGE_ALL_ICON = 'assets/icons/stage-all.svg';

export const AGENT_STAGE_META: Record<AgentStage, AgentStageMeta> = {
  demand: { label: 'Demand Planning', icon: 'assets/icons/stage-demand.svg' },
  inventory: { label: 'Inventory Optimization', icon: 'assets/icons/stage-inventory.svg' },
  replenishment: { label: 'Replenishment Service', icon: 'assets/icons/stage-replenishment.svg' }
};

export const AGENT_AUTONOMY_META: Record<AgentAutonomy, AgentTaxonomyMeta> = {
  advise: { label: 'Advise', tone: 'green' },
  augment: { label: 'Augment', tone: 'yellow' },
  automate: { label: 'Automate', tone: 'red' }
};

export const AGENT_REVERSIBILITY_META: Record<AgentReversibility, AgentReversibilityMeta> = {
  reversible: { label: 'Reversible', tone: 'grey', icon: 'assets/icons/clock-rotate-clockwise.svg' },
  adjustable: { label: 'Adjustable', tone: 'grey', icon: 'assets/icons/pen.svg' },
  resistant: { label: 'Resistant', tone: 'grey', icon: 'assets/icons/key.svg' }
};

export const AGENT_RUN_STATUS_META: Record<AgentRunStatus, AgentRunStatusMeta> = {
  completed: { label: 'Completed', tone: 'green', icon: 'assets/icons/check.svg' },
  'in-progress': { label: 'In progress', tone: 'primary', icon: 'assets/icons/arrows-spin.svg' },
  cancelled: { label: 'Cancelled', tone: 'grey', icon: 'assets/icons/close-icon.svg' }
};

export const AGENT_ACTIVATION_META: Record<AgentActivation, AgentActivationMeta> = {
  'on-demand': { label: 'On-demand', tone: 'grey', icon: 'assets/icons/arrow-through-circle-right.svg' },
  'event-driven': { label: 'Event-driven', tone: 'yellow', icon: 'assets/icons/fire-flame.svg' }
};

/** How a KPI's direction reads in a sentence, e.g. `Balanced · drops only`. */
export const AGENT_DIRECTION_LABEL: Record<AgentKpiDirection, string> = {
  down: 'drops only',
  up: 'rises only',
  both: 'both directions'
};

/** The direction control's own options, which are shorter than the sentence form above. */
export const AGENT_DIRECTION_OPTIONS: { value: AgentKpiDirection; label: string }[] = [
  { value: 'down', label: 'Drops' },
  { value: 'up', label: 'Rises' },
  { value: 'both', label: 'Both' }
];

export const AGENT_BASELINE_LABEL: Record<AgentKpiBaseline, string> = {
  'moving-average': 'moving average',
  median: 'median',
  weighted: 'demand-weighted ratio'
};

export const AGENT_CADENCE_OPTIONS: { value: AgentCadence; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

/** `null` is the uncapped option, which is why the value type is nullable. */
export const AGENT_MAX_PER_DAY_OPTIONS: { value: number | null; label: string }[] = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: null, label: 'All' }
];

/**
 * The five detection sensitivity steps.
 *
 * `blurb` is what a planner actually chooses on — each one says what the step will do to their
 * alert volume. `factor` scales the KPI's configured threshold and is only ever shown under "What
 * this means technically".
 */
export const AGENT_SENSITIVITY_META: Record<SensitivityLevel, SensitivityMeta> = {
  1: {
    label: 'Very conservative',
    tone: 'grey',
    factor: 2,
    blurb: 'Only unmistakable moves are flagged. Expect very few alerts, and expect to miss slow drift.'
  },
  2: {
    label: 'Conservative',
    tone: 'grey',
    factor: 1.5,
    blurb: 'Flags clear moves only. Fewer alerts, higher confidence in each one.'
  },
  3: {
    label: 'Balanced',
    tone: 'primary',
    factor: 1,
    blurb: 'The default. Catches material drift without flooding you — most customers start here.'
  },
  4: {
    label: 'Sensitive',
    tone: 'yellow',
    factor: 0.7,
    blurb: 'Flags earlier and smaller moves. More alerts, some will turn out to be noise.'
  },
  5: {
    label: 'Very sensitive',
    tone: 'red',
    factor: 0.5,
    blurb: 'Flags almost any movement. Use while tuning, or on a small set of critical items.'
  }
};

/** The captions at either end of the sensitivity bar. */
export const AGENT_SENSITIVITY_CAPTIONS = {
  low: 'Only the clearest signals',
  high: 'Flag early, accept noise'
};

/**
 * The automation confidence ladder, rendered disabled and labelled "Coming soon".
 *
 * Shown rather than hidden because it is the promise the feature is built around: runs are scored,
 * and the bar drops as the agent earns trust. The rungs come from the ticket, which supersedes the
 * prototype's three-mode version.
 */
export const AGENT_AUTOMATION_LADDER: AutomationStep[] = [
  {
    label: 'Always ask',
    blurb: 'Every recommendation waits for a person — nothing is applied automatically.'
  },
  {
    label: 'Acts at ≥ 90% confidence',
    blurb: 'The agent applies only what it is almost certain about, and tells you afterwards.'
  },
  {
    label: 'Acts at ≥ 75% confidence',
    blurb: 'The agent applies the clear cases on its own; the marginal ones still wait for you.'
  },
  {
    label: 'Acts at ≥ 60% confidence',
    blurb: 'The agent acts on most findings. Use once its run history has earned that much trust.'
  }
];

export const AGENT_AUTOMATION_CAPTIONS = {
  low: 'Nothing without a person',
  high: 'Acts on most findings'
};

/**
 * Why the ladder cannot be set yet.
 *
 * Deliberately does not repeat the row's helper copy above it, which already explains the scoring
 * and the trust it buys.
 */
export const AGENT_AUTOMATION_NOTE = 'Run scoring is not live yet, so this stays where it is.';
